import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Box,
} from '@mui/material';
import AppForm, { AppFormSection } from '@/__core/components/AppForm';
import { useFormLoading } from "@/__core/helpers/Loading";
import { formatErrorForSnackbar, ApiError } from "@/__core/helpers/Error";
import MessageSnackbar from "@/__core/helpers/components/MessageSnackbar";
import { useTaskPriorities } from '../hooks/useTasks';

interface PriorityCreationDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (priorityAction: any) => Promise<any>;
}

const PriorityCreationDialog: React.FC<PriorityCreationDialogProps> = ({
  open,
  onClose,
  onSave,
}) => {
  const { createTaskPriority, taskPriorities } = useTaskPriorities();
  const [priorityData, setPriorityData] = useState({
    name: "",
    color: "#6c757d", // Default color
    description: "",
    level: 1, // Priority level (1-5)
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const { loading: formLoading, handleSubmit } = useFormLoading();

  const handleSave = async () => {
    if (!priorityData.name.trim()) {
      setError("Vui lòng nhập tên độ ưu tiên");
      return;
    }

    // Find the next available level
    const existingLevels = (taskPriorities || []).map(p => p.level).sort((a, b) => a - b);
    let nextLevel = 1;
    for (let i = 0; i < existingLevels.length; i++) {
      if (existingLevels[i] === nextLevel) {
        nextLevel++;
      } else {
        break;
      }
    }

    const createPriorityData = {
      name: priorityData.name.trim(),
      color: priorityData.color,
      description: priorityData.description.trim(),
      level: nextLevel, // Use the next available level
    };
    
    
    let priorityAction: any = null;
    
    await handleSubmit(
      async () => {
        priorityAction = await createTaskPriority(createPriorityData);
        return priorityAction;
      },
      (result) => {
        setSuccess(`Đã tạo độ ưu tiên "${priorityData.name.trim()}" thành công!`);
        // Pass the Redux action directly to onSave
        onSave(priorityAction);
        resetForm();
        onClose();
      },
      (error: ApiError) => {
        const errorMessage = formatErrorForSnackbar(error);
        setError(errorMessage);
      }
    );
  };

  const resetForm = () => {
    setPriorityData({
      name: "",
      color: "#6c757d",
      description: "",
      level: 1,
    });
    setError(null);
    setSuccess(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleErrorClose = () => { setError(null); };
  const handleSuccessClose = () => { setSuccess(null); };

  const handleInputChange = (key: string, value: any) => {
    if (key === 'level') {
      const parsed = Math.max(1, Math.min(5, Number(value) || 1));
      setPriorityData((prev) => ({ ...prev, level: parsed }));
      return;
    }

    setPriorityData((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const sections: AppFormSection[] = [
    {
      key: 'priority-form',
      fields: [
        {
          key: 'name',
          label: 'Tên độ ưu tiên',
          type: 'text',
          required: true,
          placeholder: 'Nhập tên độ ưu tiên',
          gridSize: { xs: 12 },
          labelWidth: 120,
        },
        {
          key: 'color',
          label: 'Màu sắc',
          type: 'custom',
          gridSize: { xs: 12 },
          labelWidth: 120,
          render: ({ values, onChange }) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <input
                type='color'
                value={values.color || '#6c757d'}
                onChange={(e) => onChange('color', e.target.value)}
                style={{
                  width: '30px',
                  height: '30px',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
              />
              <TextField
                value={values.color || ''}
                onChange={(e) => onChange('color', e.target.value)}
                size='small'
                placeholder='#6c757d'
                sx={{ flex: 1 }}
              />
            </Box>
          ),
        },
        {
          key: 'level',
          label: 'Mức độ',
          type: 'custom',
          gridSize: { xs: 12 },
          labelWidth: 120,
          render: ({ values, onChange }) => (
            <TextField
              fullWidth
              type='number'
              value={values.level ?? 1}
              onChange={(e) => onChange('level', e.target.value)}
              size='small'
              placeholder='1-5'
              inputProps={{ min: 1, max: 5 }}
            />
          ),
        },
        {
          key: 'description',
          label: 'Mô tả',
          type: 'text',
          placeholder: 'Nhập mô tả độ ưu tiên (tùy chọn)',
          gridSize: { xs: 12 },
          labelWidth: 120,
        },
      ],
    },
  ];

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Tạo độ ưu tiên mới</DialogTitle>
        <DialogContent>
          <AppForm
            sections={sections}
            values={priorityData}
            onChange={handleInputChange}
            onSubmit={handleSave}
            onCancel={handleClose}
            submitting={formLoading}
            submitDisabled={formLoading || !priorityData.name.trim()}
            submitLabel='Tạo độ ưu tiên'
            submittingLabel='Đang tạo...'
            cancelLabel='Hủy'
            containerSx={{ p: 0, m: 0, width: '100%', backgroundColor: 'transparent' }}
          />
        </DialogContent>
      </Dialog>

      {/* Error Message */}
      <MessageSnackbar
        open={!!error}
        message={error}
        severity="error"
        onClose={handleErrorClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      {/* Success Message */}
      <MessageSnackbar
        open={!!success}
        message={success}
        severity="success"
        onClose={handleSuccessClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

export default PriorityCreationDialog;
